import type { LegendEntry } from '../types';
import type { UrlsDataType } from '../types/lib/useGetTileUrls';

const latLngToTile = (lat: number, lng: number, z: number) => {
  const
      w = Math.pow(2, (z === undefined) ? 0 : z) / 2,		// 世界全体のピクセル幅
      yrad = Math.log(Math.tan(Math.PI * (90 + lat) / 360));

  return { x: (lng / 180 + 1) * w, y: (1 - yrad / Math.PI) * w };
};

/** 洪水浸水想定区域（最大規模、計画規模）凡例 **/
const legend_shinsuishin: LegendEntry[] = [
  { r: 255, g: 255, b: 179, title: '0.3m未満の区域' },
  { r: 247, g: 245, b: 169, title: '0.3~0.5m未満の区域' },
  { r: 248, g: 225, b: 166, title: '0.5~1.0m未満の区域' },
  { r: 255, g: 216, b: 192, title: '1.0~3.0m未満の区域' },
  { r: 255, g: 183, b: 183, title: '3.0~5.0m未満の区域' },
  { r: 255, g: 145, b: 145, title: '5.0~10.0m未満の区域' }
];

/** 洪水浸水想定区域（最大規模、計画規模）凡例 **/
const legend_shinsuishin_time: LegendEntry[] = [
  { r: 97, g: 216, b: 248, title: '12時間未満の区域' },
  { r: 79, g: 141, b: 249, title: '12時間以上1日未満の区域' },
  { r: 255, g: 255, b: 75, title: '1日以上3日未満の区域' },
  { r: 255, g: 170, b: 0, title: '3日以上の区域' },
];

/** 静岡県第4次地震被害想定（レベル２重合せ図）凡例 **/
const legend_shizuoka4: LegendEntry[] = [
  // #9A63AD -> R=154, G=99, B=173
  { r: 154, g: 99, b: 173, title: '最大浸水深20.0m以上' },
  // #D79163 -> R=215, G=145, B=99
  { r: 215, g: 145, b: 99, title: '最大浸水深10.0m ～ 20.0m' },
  // #ED5C63 -> R=237, G=92, B=99
  { r: 237, g: 92, b: 99, title: '最大浸水深5.0m ～ 10.0m' },
  // #EC5CAA -> R=236, G=92, B=170
  { r: 236, g: 92, b: 170, title: '最大浸水深3.0m ～ 5.0m' },
  // #F2B8D3 -> R=242, G=184, B=211
  { r: 242, g: 184, b: 211, title: '最大浸水深2.0m ～ 3.0m' },
  // #F4B75C -> R=244, G=183, B=92
  { r: 244, g: 183, b: 92, title: '最大浸水深1.0m ～ 2.0m' },
  // #FCF255 -> R=252, G=242, B=85
  { r: 252, g: 242, b: 85, title: '最大浸水深0.3m ～ 1.0m' },
  // #4DBB7A -> R=77, G=187, B=122
  { r: 77, g: 187, b: 122, title: '最大浸水深0.01m ～ 0.3m' },
];

/** 震度凡例 **/
const legend_shindo: LegendEntry[] = [
  // #DFF1F4 -> R=223, G=241, B=244
  { r: 223, g: 241, b: 244, title: '震度3以下' },
  // #9AD8E2 -> R=154, G=216, B=226
  { r: 154, g: 216, b: 226, title: '震度4' },
  // #6180BB -> R=97, G=128, B=187
  { r: 97, g: 128, b: 187, title: '震度5弱' },
  // #96CE6B -> R=150, G=206, B=107
  { r: 150, g: 206, b: 107, title: '震度5強' },
  // #F4F074 -> R=244, G=240, B=116
  { r: 244, g: 240, b: 116, title: '震度6弱' },
  // #F4A35F -> R=244, G=163, B=95
  { r: 244, g: 163, b: 95, title: '震度6強' },
  // #EE5B5F -> R=238, G=91, B=95
  { r: 238, g: 91, b: 95, title: '震度7' },
];

/** 液状化凡例 **/
const legend_ekijyouka: LegendEntry[] = [
  // #EC4348 -> R=236, G=67, B=72
  { r: 236, g: 67, b: 72, title: '大' },
  // #F2EE60 -> R=242, G=238, B=96
  { r: 242, g: 238, b: 96, title: '中' },
  // #419661 -> R=65, G=150, B=97
  { r: 65, g: 150, b: 97, title: '小' },
  // #89C656 -> R=137, G=198, B=86
  { r: 137, g: 198, b: 86, title: 'なし' },
  // #B6B7B7 -> R=182, G=183, B=183
  { r: 182, g: 183, b: 183, title: '評価対象外' },
];

/** 凡例情報セット（tileId が キー）**/
const legends = [
  {
    id: '浸水深',
    description: '浸水深（想定）',
    colors: legend_shinsuishin
  },
  {
    id: '浸水継続時間',
    description: '浸水継続時間（想定）',
    colors: legend_shinsuishin_time
  },
  {
    // 静岡県第4次地震被害想定（レベル２重合せ図）凡例
    id: '静岡_津波_最大浸水深',
    description: '最大浸水深',
    colors: legend_shizuoka4
  },
  {
    id: '震度分布',
    description: '震度分布',
    colors: legend_shindo
  },
  {
    id: '液状化',
    description: '液状化',
    colors: legend_ekijyouka
  }
];

/**
 * getDataPng データPNGからデータを取得する関数
 * invalid: 追加無効値を相当する数値で指定．デフォルトは指定なし
 */
export const getDataPng = async (lat: number, lng: number, z: number, tileId: string, dataPngUrls: UrlsDataType[]): Promise<{ value: LegendEntry | null; description: string | null }> => {

  const dataPng = dataPngUrls.find(o => o.tileId === tileId)
  const DATA_PNG_URL = dataPng ? dataPng.tileUrl : null;

  const legend = legends.find(o => o.id === dataPng?.legendType);

  if (!DATA_PNG_URL || !legend) {
    return { value: null, description: null };
  }
  
  const p = latLngToTile(lat, lng, z);
  const x = Math.floor(p.x);            // タイルX座標
  const y = Math.floor(p.y);            // タイルY座標
  const i = (p.x - x) * 256;            // タイル内i座標
  const j = (p.y - y) * 256;            // タイル内j座標

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';      // 画像ファイルからデータを取り出すために必要です
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      let v: LegendEntry | null = null;
      
      canvas.width = 1;
      canvas.height = 1;
      context?.drawImage(img, i, j, 1, 1, 0, 0, 1, 1);
      const d = context?.getImageData(0, 0, 1, 1).data;
      
      if (d && d[3] !== 255) {
        v = null;
      } else {
        v = d ? (legend.colors.find(o => o.r === d[0] && o.g === d[1] && o.b === d[2]) ?? null) : null;
      }
      resolve({ value: v, description: legend.description });
    };

    img.onerror = () => {
      resolve({ value: null, description: null });
    };

    img.src = DATA_PNG_URL.replace('{z}', z.toString()).replace('{y}', y.toString()).replace('{x}', x.toString());
  });
};
